import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Clock, FileText, Trash2, Pencil, AlertCircle, X, Repeat } from 'lucide-react';
import { formatToFieldTimezone } from '../utils/timezoneUtils';
import { applyStartChange } from '../utils/blockDuration';
import { bookingService } from '../apiClient';
import { STATUS_CONFIG } from '../shared/statusConfig';
import { MAX_OCCURRENCES, DEFAULT_RECURRENCE, countOccurrences } from '../utils/recurrenceUtils';

export { MAX_OCCURRENCES, DEFAULT_RECURRENCE };

// Normalize a datetime-local value ("yyyy-MM-ddTHH:mm") to a full ISO string.
// Sent without a UTC offset so the backend treats it as field-local time.
export const toISO = (dt) => dt ? (dt.length === 16 ? `${dt}:00` : dt) : '';

const toDatetimeLocalValue = (utcDate, timezone) => {
  const { localDate, localTime } = formatToFieldTimezone(utcDate, timezone);
  return localDate && localTime ? `${localDate}T${localTime}` : '';
};

export const BLOCKED_CFG = STATUS_CONFIG.blocked;

// ---------------------------------------------------------------------------
// Shared modal primitives (also used by EventDetailModal in OperatorBookingCalendar)
// ---------------------------------------------------------------------------
export const StatusBadge = ({ cfg }) => (
  <span className="badge rounded-pill fw-medium mb-2 px-3 py-1" style={{ backgroundColor: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`, fontSize: '0.75rem' }}>
    <span className="me-1" style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.dot, verticalAlign: 'middle' }} />
    {cfg.label}
  </span>
);

// How far down the sheet must be dragged (in px) before releasing dismisses it
// instead of snapping back.
const DRAG_DISMISS_THRESHOLD = 100;

// `dismissOnDrag` is off for forms (e.g. CreateBlockModal): a thumb grazing the
// handle while reaching for a field below would otherwise silently discard
// whatever the operator had typed, with no undo.
// Lets ModalHeader know whether the body underneath it is scrolled, so it can
// show a separator only once there's content sliding under it.
const ScrolledContext = React.createContext(false);

export const ModalShell = ({ cfg, isMobile, onClose, maxWidth = 440, dismissOnDrag = true, children }) => {
  const sheetRef = useRef(null);
  const dragRef = useRef({ dragging: false, startY: 0, y: 0 });
  const [scrolled, setScrolled] = useState(false);

  // Mutate the sheet's transform directly on the DOM node instead of going
  // through React state, so a drag doesn't re-render the modal's contents
  // (forms, dropdowns, etc.) on every pointermove.
  const applyTransform = (y, animate) => {
    const el = sheetRef.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
    el.style.transform = y ? `translateY(${y}px)` : '';
  };

  const handleDragStart = (e) => {
    dragRef.current = { startY: e.clientY, dragging: true, y: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleDragMove = (e) => {
    if (!dragRef.current.dragging) return;
    const y = Math.max(0, e.clientY - dragRef.current.startY);
    dragRef.current.y = y;
    applyTransform(y, false);
  };

  const handleDragEnd = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    if (dragRef.current.y > DRAG_DISMISS_THRESHOLD) onClose();
    else applyTransform(0, true);
  };

  // Lock body scroll while a mobile sheet covers the screen, so a drag on
  // the backdrop can't scroll the page underneath it. `overflow: hidden`
  // alone doesn't stop touch-scroll on iOS Safari, so pin the body in place
  // and restore its scroll position on close. Lives here (not per-caller) so
  // every mobile sheet built on ModalShell gets it, not just one screen's.
  useEffect(() => {
    if (!isMobile) return;
    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isMobile]);

  return (
    <>
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{ backgroundColor: 'var(--modal-backdrop-navy)', backdropFilter: 'blur(4px)', zIndex: 1050 }}
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        className={`position-fixed bg-white ${isMobile ? 'bottom-0 start-0 w-100 rounded-top-4' : 'top-50 start-50 translate-middle rounded-4'}`}
        style={{
          zIndex: 1060,
          maxWidth: isMobile ? '100%' : maxWidth,
          width: '100%',
          maxHeight: isMobile ? '85dvh' : '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          transition: 'transform 0.2s ease-out',
        }}
      >
        <div style={{ height: 4, flexShrink: 0, borderRadius: '12px 12px 0 0', background: `linear-gradient(90deg, ${cfg.stripe}, color-mix(in srgb, ${cfg.stripe} 53%, transparent))` }} />
        <div
          className="p-4 pb-5"
          style={{ overflowY: 'auto', overscrollBehavior: 'contain', flex: '1 1 auto', minHeight: 0 }}
          onScroll={e => setScrolled(e.currentTarget.scrollTop > 0)}
        >
          {isMobile && (
            <div
              className="mx-auto mb-4 d-flex justify-content-center"
              style={dismissOnDrag ? { touchAction: 'none', cursor: 'grab', padding: '8px 0', marginTop: -8 } : { padding: '8px 0', marginTop: -8 }}
              onPointerDown={dismissOnDrag ? handleDragStart : undefined}
              onPointerMove={dismissOnDrag ? handleDragMove : undefined}
              onPointerUp={dismissOnDrag ? handleDragEnd : undefined}
              onPointerCancel={dismissOnDrag ? handleDragEnd : undefined}
            >
              <div className="rounded-pill bg-light" style={{ width: 40, height: 5 }} />
            </div>
          )}
          <ScrolledContext.Provider value={scrolled}>{children}</ScrolledContext.Provider>
        </div>
      </div>
    </>
  );
};

// Sticky within ModalShell's scrollable body (via negative margins that bleed
// into its p-4 padding) so the title and close button stay reachable once a
// tall form scrolls — otherwise both scroll out of view with the content.
export const ModalHeader = ({ cfg, title, onClose, children }) => {
  const scrolled = useContext(ScrolledContext);
  return (
    <div
      className="d-flex justify-content-between align-items-start"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'var(--bg-surface)',
        margin: '0 0 0',
        padding: '0 1rem 1rem',
        boxShadow: scrolled ? '0 4px 6px -6px var(--shadow-color)' : '0 4px 6px -6px transparent',
        transition: 'box-shadow 0.15s ease',
      }}
    >
      <div className="flex-grow-1 min-w-0 me-3">
        <StatusBadge cfg={cfg} />
        <h5 className="fw-bold mb-1" style={{ color: 'var(--text-primary)', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{title}</h5>
        {children}
      </div>
      <button onClick={onClose} className="btn btn-light rounded-circle d-flex align-items-center justify-content-center p-0 border flex-shrink-0" style={{ width: 36, height: 36, color: 'var(--text-secondary)' }}>
        <X size={16} />
      </button>
    </div>
  );
};

export const DetailCard = ({ children }) => (
  <div className="rounded-4 px-3 py-1" style={{ backgroundColor: 'var(--bg-surface-alt)', border: '1px solid var(--border-color)' }}>
    {children}
  </div>
);

// Shared caption style for a form section's small uppercase label (Start/End/
// Repeat/Fields/Apply to/...), so every section header lines up identically.
const SECTION_LABEL_STYLE = { fontSize: '0.68rem', letterSpacing: '0.06em', color: 'var(--text-secondary)' };

// Shared selected/unselected coloring for a toggleable pill or chip button
// (listing picker, repeat day picker, edit-scope picker) so they stay in sync.
const togglePillStyle = (selected) => (selected
  ? { backgroundColor: 'var(--field-green)', color: '#fff', border: '1px solid var(--field-green)' }
  : { backgroundColor: 'var(--bg-surface-alt)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' });

export const DetailRow = ({ icon, label, children, isLast }) => (
  <div className={`d-flex align-items-center gap-3 py-3 ${isLast ? '' : 'border-bottom'}`} style={{ borderColor: 'var(--border-color)' }}>
    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 36, height: 36, backgroundColor: 'var(--bg-surface-alt)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>
      {React.cloneElement(icon, { size: 16 })}
    </div>
    <div className="d-flex flex-column min-w-0">
      {label && <span className="text-uppercase fw-semibold" style={SECTION_LABEL_STYLE}>{label}</span>}
      <span className="fw-medium" style={{ color: 'var(--text-primary)', fontSize: '0.9rem', overflowWrap: 'break-word' }}>{children}</span>
    </div>
  </div>
);

// Defaults `daysOfWeek` to the start date's own weekday if Weekly was picked
// but no day was ever toggled.
const serializeRecurrence = (recurrence, startDateTime) => {
  if (recurrence.frequency === 'weekly' && recurrence.daysOfWeek.length === 0) {
    const startWeekday = DateTime.fromISO(toISO(startDateTime)).weekday;
    return { ...recurrence, daysOfWeek: [startWeekday === 7 ? 0 : startWeekday] };
  }
  return recurrence;
};

export const useCreateBlocks = ({ operatorID, onSuccess }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ listingIDs, startDateTime, endDateTime, reason, recurrence }) => {
      const isRecurring = !!recurrence && recurrence.frequency !== 'none';
      const results = await Promise.allSettled(
        listingIDs.map(lid =>
          (isRecurring
            ? bookingService.createBlockSeries(operatorID, lid, {
                startDateTime: toISO(startDateTime),
                endDateTime: toISO(endDateTime),
                reason,
                recurrence: serializeRecurrence(recurrence, startDateTime),
              })
            : bookingService.createBlock(operatorID, lid, {
                startDateTime: toISO(startDateTime),
                endDateTime: toISO(endDateTime),
                reason,
              })
          // Tag the rejection with its listing so a conflict error can be
          // formatted in that listing's own timezone.
          ).catch(err => Promise.reject({ ...err, listingID: lid }))
        )
      );

      // Invalidate every listing that succeeded, even if others in this submit failed.
      listingIDs.forEach((lid, i) => {
        if (results[i].status === 'fulfilled') {
          queryClient.invalidateQueries(['operator', operatorID, 'listing', lid, 'blocks']);
        }
      });

      const firstFailure = results.find(r => r.status === 'rejected');
      if (firstFailure) throw firstFailure.reason;
      return results.map(r => r.value);
    },
    onSuccess: () => { onSuccess?.(); },
  });
};

// Hook: edit a single block — always detaches it from its series server-side
// (see PUT /blocks/:blockID), so a later bulk series update won't overwrite it.
export const useUpdateBlock = ({ operatorID, onSuccess } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listingID, blockID, startDateTime, endDateTime, reason }) =>
      bookingService.updateBlock(operatorID, listingID, blockID, {
        startDateTime: toISO(startDateTime),
        endDateTime: toISO(endDateTime),
        reason,
      }),
    onSuccess: (data, { listingID }) => {
      queryClient.invalidateQueries(['operator', operatorID, 'listing', listingID, 'blocks']);
      onSuccess?.(data);
    },
  });
};

// Hook: bulk-update a series "this and following" — a reason-only edit updates
// in place, a recurrence change regenerates the future occurrences.
export const useUpdateBlockSeries = ({ operatorID, onSuccess } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listingID, seriesID, fromBlockID, startDateTime, endDateTime, reason, recurrence }) =>
      bookingService.updateBlockSeries(operatorID, listingID, seriesID, {
        fromBlockID,
        reason,
        ...(recurrence ? {
          startDateTime: toISO(startDateTime),
          endDateTime: toISO(endDateTime),
          recurrence: serializeRecurrence(recurrence, startDateTime),
        } : {}),
      }),
    onSuccess: (data, { listingID }) => {
      queryClient.invalidateQueries(['operator', operatorID, 'listing', listingID, 'blocks']);
      onSuccess?.(data);
    },
  });
};

// Hook: delete a single block, or a whole series — two explicit mutations so
// the caller decides, and a series-tagged block can still be deleted on its own.
export const useDeleteBlock = ({ operatorID }) => {
  const queryClient = useQueryClient();
  const invalidate = (listingID) =>
    queryClient.invalidateQueries(['operator', operatorID, 'listing', listingID, 'blocks']);

  const deleteBlockMutation = useMutation({
    mutationFn: ({ listingID, blockID }) => bookingService.deleteBlock(operatorID, listingID, blockID),
    onSuccess: (_, { listingID }) => invalidate(listingID),
  });
  const deleteSeriesMutation = useMutation({
    mutationFn: ({ listingID, seriesID }) => bookingService.deleteBlockSeries(operatorID, listingID, seriesID),
    onSuccess: (_, { listingID }) => invalidate(listingID),
  });

  return {
    deleteBlock: (b) => deleteBlockMutation.mutate({ listingID: b.listingID, blockID: b.blockID }),
    deleteSeries: (b) => deleteSeriesMutation.mutate({ listingID: b.listingID, seriesID: b.seriesID }),
    isDeleting: deleteBlockMutation.isPending || deleteSeriesMutation.isPending,
  };
};

// `listingTimezone` should be the timezone of the listing that produced the
// error, so conflict dates read correctly.
export const formatBlockError = (error, listingTimezone) => {
  if (!error) return '';
  if (Array.isArray(error.conflicts) && error.conflicts.length > 0) {
    const dates = error.conflicts
      .map(c => formatToFieldTimezone(c.startDateTime, listingTimezone).date)
      .join(', ');
    const n = error.conflicts.length;
    return `${n} occurrence${n > 1 ? 's' : ''} conflict${n > 1 ? '' : 's'} with existing blocks (${dates})`;
  }
  // `.status` marks a real API error response; a raw client-side exception has none
  // and shouldn't leak its technical message to the operator.
  if (error.status && error.message) return error.message;
  return 'Failed to save block. Please try again.';
};

// ---------------------------------------------------------------------------
// Block modals
// ---------------------------------------------------------------------------
export const BlockDetailModal = ({ block, onClose, isMobile, onDeleteBlock, onDeleteSeries, onEdit, isDeleting }) => {
  const tz = block.listingTimezone || 'America/New_York';
  const start = formatToFieldTimezone(block.startDateTime, tz);
  const end = formatToFieldTimezone(block.endDateTime, tz);
  const isSeries = !!block.seriesID;
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <ModalShell cfg={BLOCKED_CFG} isMobile={isMobile} onClose={onClose} maxWidth={400}>
      <ModalHeader cfg={BLOCKED_CFG} title={block.listingName || 'Time Block'} onClose={onClose} />
      <DetailCard>
        <DetailRow label="Date & Time" icon={<Clock />} isLast={!block.reason && !isSeries}>
          {start.date} &nbsp;·&nbsp; {start.time} – {end.time}
        </DetailRow>
        {isSeries && (
          <DetailRow label="Recurrence" icon={<Repeat />} isLast={!block.reason}>
            Part of a recurring series
          </DetailRow>
        )}
        {block.reason && (
          <DetailRow label="Reason" icon={<FileText />} isLast>
            {block.reason}
          </DetailRow>
        )}
      </DetailCard>

      {confirmingDelete ? (
        <div className="mt-3 p-3 rounded-3" style={{ backgroundColor: 'var(--bs-danger-bg-subtle)' }}>
          <p className="mb-2" style={{ fontSize: '0.85rem', color: 'var(--bs-danger-text-emphasis)' }}>
            {isSeries
              ? "Remove just this block, or every occurrence in the recurring series? This can't be undone."
              : "This removes this time block. This can't be undone."}
          </p>
          <div className={`d-flex gap-2 ${isMobile ? 'flex-column' : 'flex-wrap'}`}>
            <button
              className="btn btn-sm flex-grow-1 rounded-pill fw-semibold text-white"
              style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
              onClick={onDeleteBlock}
              disabled={isDeleting}
            >
              {isDeleting ? 'Removing…' : isSeries ? 'This Block' : 'Remove Block'}
            </button>
            {isSeries && (
              <button
                className="btn btn-sm flex-grow-1 rounded-pill fw-semibold text-white"
                style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                onClick={onDeleteSeries}
                disabled={isDeleting}
              >
                {isDeleting ? 'Removing…' : 'Entire Series'}
              </button>
            )}
            <button
              className="btn btn-sm btn-light flex-grow-1 rounded-pill fw-semibold"
              onClick={() => setConfirmingDelete(false)}
              disabled={isDeleting}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="d-flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <button
            className="btn btn-link text-decoration-none flex-grow-1 d-flex align-items-center justify-content-center gap-2"
            style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}
            onClick={onEdit}
          >
            <Pencil size={14} />
            Edit
          </button>
          <button
            className="btn btn-link text-decoration-none flex-grow-1 d-flex align-items-center justify-content-center gap-2"
            style={{ color: '#dc2626', fontSize: '0.875rem' }}
            onClick={() => setConfirmingDelete(true)}
            disabled={isDeleting}
          >
            <Trash2 size={14} />
            Remove
          </button>
        </div>
      )}
    </ModalShell>
  );
};

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const FREQUENCY_UNIT = { daily: 'day', weekly: 'week', monthly: 'month' };

const RepeatDayPicker = ({ daysOfWeek, onToggle }) => (
  <div className="d-flex justify-content-between gap-1">
    {DAY_LABELS.map((label, i) => {
      const selected = daysOfWeek.includes(i);
      return (
        <button
          key={i}
          type="button"
          onClick={() => onToggle(i)}
          className="btn btn-sm rounded-circle fw-medium d-flex align-items-center justify-content-center"
          style={{ flex: '0 1 36px', aspectRatio: '1 / 1', minWidth: 0, padding: 0, fontSize: '0.8rem', ...togglePillStyle(selected) }}
        >
          {label}
        </button>
      );
    })}
  </div>
);

// Shared style for the Start/End datetime-local inputs, so both stay in sync.
const DATE_INPUT_STYLE = { color: 'var(--text-primary)', fontSize: '0.9rem', boxShadow: 'none' };

const DateTimeField = ({ label, value, onChange, bordered = true, type = 'datetime-local' }) => (
  <div className="py-2" style={bordered ? { borderBottom: '1px solid var(--border-color)' } : undefined}>
    <span className="d-block text-uppercase fw-semibold mb-1" style={SECTION_LABEL_STYLE}>{label}</span>
    <input
      type={type}
      className="form-control border-0 bg-transparent p-0 fw-medium"
      style={DATE_INPUT_STYLE}
      value={value}
      onChange={onChange}
    />
  </div>
);

const datePart = (dt) => (dt || '').slice(0, 10);
const timePart = (dt) => (dt || '').slice(11, 16);

// End at 23:59 rather than next midnight so it reads as "12:00 AM – 11:59 PM" on one date.
const allDayRange = (date) => (date
  ? { startDateTime: `${date}T00:00`, endDateTime: `${date}T23:59` }
  : { startDateTime: '', endDateTime: '' });

const timedRange = (date) => ({ startDateTime: `${date}T09:00`, endDateTime: `${date}T11:00` });

// Start/End date-time inputs plus (when `showRepeat`) the full recurrence editor,
// shared by CreateBlockModal and EditBlockModal's "this and following" schedule change.
const DateTimeRepeatCard = ({ form, setForm, showRepeat }) => {
  const { recurrence, startDateTime, endDateTime, allDay } = form;
  const isRecurring = showRepeat && recurrence.frequency !== 'none';
  const projectedOccurrenceCount = isRecurring
    ? countOccurrences({ startDateTime: toISO(startDateTime), ...recurrence })
    : 1;
  // Surface a too-large interval (never reaches "After N occurrences") here
  // instead of letting the operator submit and hit a 422.
  const occurrenceCountShortfall = isRecurring
    && recurrence.endType === 'after'
    && projectedOccurrenceCount < recurrence.endCount;

  const setRecurrence = (patch) => setForm(f => ({ ...f, recurrence: { ...f.recurrence, ...patch } }));

  const handleFrequencyChange = (value) => {
    const patch = { frequency: value };
    if (value === 'weekly' && recurrence.daysOfWeek.length === 0 && startDateTime) {
      const startWeekday = DateTime.fromISO(toISO(startDateTime)).weekday;
      patch.daysOfWeek = [startWeekday === 7 ? 0 : startWeekday];
    }
    setRecurrence(patch);
  };

  const toggleDayOfWeek = (day) => setRecurrence({
    daysOfWeek: recurrence.daysOfWeek.includes(day)
      ? recurrence.daysOfWeek.filter(d => d !== day)
      : [...recurrence.daysOfWeek, day].sort(),
  });

  return (
    <div className="rounded-4 px-3 py-1 mb-1" style={{ backgroundColor: 'var(--bg-surface-alt)', border: '1px solid var(--border-color)' }}>
      {allDay ? (
        <DateTimeField
          type="date"
          label="Date"
          value={datePart(startDateTime)}
          onChange={e => setForm(f => ({ ...f, ...allDayRange(e.target.value) }))}
          bordered={showRepeat}
        />
      ) : (
        <>
          <DateTimeField
            label="Start"
            value={startDateTime}
            onChange={e => setForm(f => applyStartChange(f, e.target.value))}
          />
          <DateTimeField
            label="End"
            value={endDateTime}
            onChange={e => setForm(f => ({ ...f, endDateTime: e.target.value }))}
            bordered={showRepeat}
          />
        </>
      )}

      {showRepeat && (
        <>
          <div className="py-2" style={isRecurring ? { borderBottom: '1px solid var(--border-color)' } : undefined}>
            <div className="d-flex align-items-center justify-content-between">
              <span className="d-flex align-items-center gap-1 text-uppercase fw-semibold" style={SECTION_LABEL_STYLE}>
                <Repeat size={12} /> Repeat
              </span>
              <select
                className="form-select form-select-sm w-auto fw-medium"
                style={{ fontSize: '0.85rem', paddingRight: '2rem' }}
                value={recurrence.frequency}
                onChange={e => handleFrequencyChange(e.target.value)}
              >
                <option value="none">Does Not Repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {isRecurring && (
            <div className="py-2 d-flex flex-column gap-2">
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>Every</span>
                <input
                  type="number"
                  min={1}
                  className="form-control form-control-sm text-center"
                  style={{ width: 56, fontSize: '0.82rem' }}
                  value={recurrence.interval}
                  onChange={e => setRecurrence({ interval: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                />
                <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>
                  {FREQUENCY_UNIT[recurrence.frequency]}{recurrence.interval === 1 ? '' : 's'}
                </span>
              </div>

              {recurrence.frequency === 'weekly' && (
                <RepeatDayPicker daysOfWeek={recurrence.daysOfWeek} onToggle={toggleDayOfWeek} />
              )}

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>End</span>
                <select
                  className="form-select form-select-sm w-auto"
                  style={{ fontSize: '0.82rem', paddingRight: '2rem' }}
                  value={recurrence.endType}
                  onChange={e => setRecurrence({ endType: e.target.value })}
                >
                  <option value="after">After</option>
                  <option value="on_date">On Date</option>
                </select>
                {recurrence.endType === 'after' ? (
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={MAX_OCCURRENCES}
                      className="form-control form-control-sm text-center"
                      style={{ width: 64, fontSize: '0.82rem' }}
                      value={recurrence.endCount}
                      onChange={e => setRecurrence({ endCount: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    />
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-primary)' }}>occurrences</span>
                  </div>
                ) : (
                  <input
                    type="date"
                    className="form-control form-control-sm w-auto"
                    style={{ fontSize: '0.82rem' }}
                    min={startDateTime ? startDateTime.slice(0, 10) : undefined}
                    value={recurrence.endDate}
                    onChange={e => setRecurrence({ endDate: e.target.value })}
                  />
                )}
              </div>

              <p className="mb-0" style={{ fontSize: '0.75rem', color: (projectedOccurrenceCount > MAX_OCCURRENCES || occurrenceCountShortfall) ? 'var(--bs-danger-text-emphasis)' : 'var(--text-secondary)' }}>
                {projectedOccurrenceCount > MAX_OCCURRENCES
                  ? `Exceeds the ${MAX_OCCURRENCES}-occurrence limit — narrow the range or lower the count.`
                  : occurrenceCountShortfall
                  ? `This recurrence only reaches ${projectedOccurrenceCount} of the requested ${recurrence.endCount} occurrences within 10 years — lower the interval or the occurrence count.`
                  : `This will create ${projectedOccurrenceCount} block${projectedOccurrenceCount === 1 ? '' : 's'}.`}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const isRecurrenceValid = (recurrence, startDateTime) => {
  const projectedOccurrenceCount = countOccurrences({ startDateTime: toISO(startDateTime), ...recurrence });
  const occurrenceCountShortfall = recurrence.endType === 'after' && projectedOccurrenceCount < recurrence.endCount;
  return recurrence.frequency !== 'none'
    && recurrence.interval >= 1
    && (recurrence.frequency !== 'weekly' || recurrence.daysOfWeek.length > 0)
    && (recurrence.endType === 'after' ? recurrence.endCount >= 1 : !!recurrence.endDate)
    && projectedOccurrenceCount > 0 && projectedOccurrenceCount <= MAX_OCCURRENCES
    && !occurrenceCountShortfall;
};

export const CreateBlockModal = ({ form, setForm, onClose, onSubmit, isMobile, listings, isLoading, error }) => {
  const { recurrence } = form;
  const isRecurring = recurrence.frequency !== 'none';

  const isValid = form.listingIDs.length > 0 && form.startDateTime && form.endDateTime
    && toISO(form.endDateTime) > toISO(form.startDateTime)
    && (!isRecurring || isRecurrenceValid(recurrence, form.startDateTime));

  const toggleListing = (id) => setForm(f => ({
    ...f,
    listingIDs: f.listingIDs.includes(id) ? f.listingIDs.filter(x => x !== id) : [...f.listingIDs, id],
  }));

  // Preserve the operator's timed Start/End across the toggle via `savedTimes`.
  const setAllDay = (checked) => setForm(f => {
    const date = datePart(f.startDateTime) || DateTime.local().toFormat('yyyy-MM-dd');
    if (checked) {
      return {
        ...f,
        allDay: true,
        savedTimes: { start: timePart(f.startDateTime), end: timePart(f.endDateTime) },
        ...allDayRange(date),
      };
    }
    const saved = f.savedTimes;
    const restored = saved?.start && saved?.end
      ? { startDateTime: `${date}T${saved.start}`, endDateTime: `${date}T${saved.end}` }
      : timedRange(date);
    return { ...f, allDay: false, savedTimes: undefined, ...restored };
  });

  return (
    <ModalShell cfg={BLOCKED_CFG} isMobile={isMobile} onClose={onClose} maxWidth={420} dismissOnDrag={false}>
      <ModalHeader cfg={BLOCKED_CFG} title="Block Time" onClose={onClose} />

      <div className="form-check form-switch d-flex align-items-center gap-2 mb-3">
        <input
          className="form-check-input mt-0"
          type="checkbox"
          role="switch"
          id="blockAllDay"
          checked={!!form.allDay}
          onChange={e => setAllDay(e.target.checked)}
          style={{ width: '2.5rem', height: '1.25rem', cursor: 'pointer' }}
        />
        <label className="form-check-label fw-semibold mb-0" htmlFor="blockAllDay" style={{ fontSize: '0.85rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
          All day
        </label>
      </div>

      {/* Field multi-selector — always shown so operator knows which field(s) are affected */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <span className="text-uppercase fw-semibold" style={SECTION_LABEL_STYLE}>
            Fields {listings.length > 1 && <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— select all that apply</span>}
          </span>
          {listings.length > 1 && (
            <button
              type="button"
              className="btn btn-link p-0 fw-medium"
              style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textDecoration: 'none' }}
              onClick={() => setForm(f => ({
                ...f,
                listingIDs: f.listingIDs.length === listings.length ? [] : listings.map(l => l.listingID),
              }))}
            >
              {form.listingIDs.length === listings.length ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2">
          {listings.map(l => {
            const selected = form.listingIDs.includes(l.listingID);
            return (
              <button
                key={l.listingID}
                type="button"
                onClick={() => toggleListing(l.listingID)}
                className="btn btn-sm rounded-pill fw-medium"
                style={togglePillStyle(selected)}
              >
                {l.listingName}
              </button>
            );
          })}
        </div>
      </div>

      <DateTimeRepeatCard form={form} setForm={setForm} showRepeat />

      {/* Timezone context — derived from selected listings */}
      {(() => {
        const sel = listings.filter(l => form.listingIDs.includes(l.listingID));
        const tzs = [...new Set(sel.map(l => l.listingTimezone).filter(Boolean))];
        if (!tzs.length) return <div className="mb-2" />;
        if (tzs.length === 1) return (
          <p className="mb-2 mt-2" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', paddingLeft: '2px' }}>
            Times are in field time · <strong>{tzs[0]}</strong>
          </p>
        );
        return (
          <p className="mb-2 mt-2" style={{ fontSize: '0.75rem', color: 'var(--bs-warning-text-emphasis)', paddingLeft: '2px' }}>
            These fields span multiple timezones — the same wall-clock time will be blocked in each field's local timezone
          </p>
        );
      })()}

      <input
        type="text"
        className="form-control rounded-3 mb-3"
        placeholder="Reason (optional — only visible to you)"
        value={form.reason}
        onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
        style={{ fontSize: '0.9rem' }}
      />

      {error && (
        <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded-3" style={{ backgroundColor: 'var(--bs-danger-bg-subtle)', color: 'var(--bs-danger-text-emphasis)', fontSize: '0.85rem' }}>
          <AlertCircle size={14} className="flex-shrink-0" />
          {error}
        </div>
      )}

      <button
        className="btn w-100 rounded-pill fw-semibold text-white"
        style={{ backgroundColor: BLOCKED_CFG.stripe, borderColor: BLOCKED_CFG.stripe, opacity: (!isValid || isLoading) ? 0.6 : 1 }}
        onClick={onSubmit}
        disabled={!isValid || isLoading}
      >
        {isLoading ? 'Saving…' : isRecurring ? 'Create Recurring Block' : 'Block Time'}
      </button>
    </ModalShell>
  );
};

const EDIT_SCOPE_OPTIONS = [
  { value: 'this', label: 'This occurrence' },
  { value: 'following', label: 'This and following' },
];

// Edits a single block, or (for a series block) a chosen scope: just this
// occurrence, or this and every future occurrence.
export const EditBlockModal = ({ block, onClose, onSubmit, isMobile, isLoading, error }) => {
  const isSeries = !!block.seriesID;
  const tz = block.listingTimezone || 'America/New_York';

  const [form, setForm] = useState(() => ({
    scope: 'this',
    changeSchedule: false,
    startDateTime: toDatetimeLocalValue(block.startDateTime, tz),
    endDateTime: toDatetimeLocalValue(block.endDateTime, tz),
    reason: block.reason || '',
    // Prefill from the block's own series (joined in by GET .../blocks) so
    // "change the time or repeat pattern" starts from the current rule, not blank.
    recurrence: isSeries ? {
      frequency: block.seriesFrequency,
      interval: block.seriesInterval,
      daysOfWeek: block.seriesDaysOfWeek ? block.seriesDaysOfWeek.split(',').map(Number) : [],
      endType: block.seriesEndType,
      endCount: block.seriesEndCount ?? DEFAULT_RECURRENCE.endCount,
      endDate: block.seriesEndDate ? block.seriesEndDate.slice(0, 10) : '',
    } : DEFAULT_RECURRENCE,
  }));

  const isRecurring = form.scope === 'following' && form.changeSchedule;
  const needsDateValidity = form.scope === 'this' || isRecurring;
  const datesValid = !needsDateValidity
    || (form.startDateTime && form.endDateTime && toISO(form.endDateTime) > toISO(form.startDateTime));
  const isValid = datesValid && (!isRecurring || isRecurrenceValid(form.recurrence, form.startDateTime));

  const handleSubmit = () => {
    if (form.scope === 'this') {
      onSubmit({ scope: 'this', startDateTime: form.startDateTime, endDateTime: form.endDateTime, reason: form.reason });
    } else if (!form.changeSchedule) {
      onSubmit({ scope: 'following', reason: form.reason });
    } else {
      onSubmit({ scope: 'following', startDateTime: form.startDateTime, endDateTime: form.endDateTime, reason: form.reason, recurrence: form.recurrence });
    }
  };

  return (
    <ModalShell cfg={BLOCKED_CFG} isMobile={isMobile} onClose={onClose} maxWidth={420} dismissOnDrag={false}>
      <ModalHeader cfg={BLOCKED_CFG} title={block.listingName || 'Edit Block'} onClose={onClose} />

      {isSeries && (
        <div className="mb-3">
          <span className="d-block text-uppercase fw-semibold mb-2" style={SECTION_LABEL_STYLE}>Apply to</span>
          <div className="d-flex gap-2">
            {EDIT_SCOPE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm(f => ({ ...f, scope: opt.value }))}
                className="btn btn-sm rounded-pill fw-medium flex-grow-1"
                style={togglePillStyle(form.scope === opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {form.scope === 'following' && (
        <label className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
          <input
            type="checkbox"
            className="form-check-input m-0"
            checked={form.changeSchedule}
            onChange={e => setForm(f => ({ ...f, changeSchedule: e.target.checked }))}
          />
          Also change the time or repeat pattern
        </label>
      )}

      {(form.scope === 'this' || form.changeSchedule) && (
        <DateTimeRepeatCard form={form} setForm={setForm} showRepeat={form.scope === 'following'} />
      )}

      <input
        type="text"
        className="form-control rounded-3 mb-3 mt-1"
        placeholder="Reason (optional — only visible to you)"
        value={form.reason}
        onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
        style={{ fontSize: '0.9rem' }}
      />

      {error && (
        <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded-3" style={{ backgroundColor: 'var(--bs-danger-bg-subtle)', color: 'var(--bs-danger-text-emphasis)', fontSize: '0.85rem' }}>
          <AlertCircle size={14} className="flex-shrink-0" />
          {error}
        </div>
      )}

      <button
        className="btn w-100 rounded-pill fw-semibold text-white"
        style={{ backgroundColor: BLOCKED_CFG.stripe, borderColor: BLOCKED_CFG.stripe, opacity: (!isValid || isLoading) ? 0.6 : 1 }}
        onClick={handleSubmit}
        disabled={!isValid || isLoading}
      >
        {isLoading ? 'Saving…' : 'Save Changes'}
      </button>
    </ModalShell>
  );
};
